import { Bijection } from "./bijection";

/**
 * Abstract asynchronous storage for a single value.
 * This may perform automatic encoding/decoding operations on the underlying
 * stored data.
 */
export interface AsyncStore<T> {
  load(params?: { signal?: AbortSignal }): Promise<T>;
  save(value: T, params?: { signal?: AbortSignal }): Promise<void>;
}

/**
 * Abstract synchronous storage for a single value.
 * This may perform automatic encoding/decoding operations on the underlying
 * stored data.
 */
export interface Store<T> {
  /**
   * Get the value.
   */
  get(): T;
  /**
   * Set the value.
   */
  set(value: T): void;
}

export function basicAsyncStore<T>(initial: T): AsyncStore<T> {
  let value = initial;
  return {
    async load() {
      return value;
    },
    async save(newValue) {
      value = newValue;
    },
  };
}

/**
 * Apply a bijective transform to the store.
 */
export function storeTransform<A, B>(
  store: Store<A>,
  transform: Bijection<A, B>,
): Store<B> {
  return {
    get() {
      return transform.read(store.get());
    },
    set(value) {
      store.set(transform.write(value));
    },
  };
}
